import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { itemService } from '../../../services/menuitemService';
import { ConfirmDialog } from './UIComponents';
import toast from 'react-hot-toast';
import {
  X, TrendingUp, TrendingDown,
  RotateCcw, Save, Search, Filter, Tags,
} from 'lucide-react';

// ── Pure helpers (module-level, no hooks) ──

function resolveVariantPrice(variant) {
  return variant.variantPrice ?? variant.basePrice ?? variant.price ?? 0;
}

function roundPrice(value) {
  return Math.round(value);
}

function priceChanged(edited, original) {
  return edited !== undefined && Number(edited) !== Number(original ?? 0);
}

function clampPrice(value) {
  const num = Number(value);
  if (isNaN(num) || num < 0) return 0;
  return roundPrice(num);
}

// ── PriceCell ──

const PriceCell = React.memo(function PriceCell({ value, original, itemId, field, variantId, onCommit }) {
  const [local, setLocal] = useState(value ?? '');
  const prevValueRef = useRef(value);

  useEffect(() => {
    if (prevValueRef.current !== value) {
      setLocal(value ?? '');
      prevValueRef.current = value;
    }
  }, [value]);

  const handleBlur = useCallback(() => {
    if (String(local) !== String(value)) {
      onCommit(itemId, field, clampPrice(local), variantId);
    }
  }, [local, value, onCommit, itemId, field, variantId]);

  const changed = Number(local) !== Number(original);

  return (
    <div className="flex items-center gap-1.5 justify-center">
      <input
        type="number"
        min="0"
        step="1"
        value={local}
        onChange={e => setLocal(e.target.value)}
        onBlur={handleBlur}
        className={`w-[68px] px-2 py-1 text-right text-xs rounded-md border outline-none transition-all tabular-nums
          ${changed
            ? 'border-teal-400 bg-teal-50 text-teal-900 ring-1 ring-teal-200'
            : 'border-slate-200 bg-white text-slate-700 focus:border-teal-400 focus:ring-1 focus:ring-teal-200'
          }`}
      />
      {changed ? (
        <span className="text-[10px] text-slate-400 tabular-nums w-8 text-left">
          {Number(original).toFixed(0)}
        </span>
      ) : (
        <span className="w-8" />
      )}
    </div>
  );
});

// ── ItemRow: memoized per-item, variants inline within cells ──

const ItemRow = React.memo(function ItemRow({ item, itemEdit, onCommit }) {
  const hasVariants = item.variants?.length > 0;
  const edit = itemEdit || {};
  const editVariants = edit.variants || [];

  // Build rows: [{label, dine, dineOrig, take, takeOrig, del, delOrig, variantId?}]
  const rows = [];

  // Item base price row
  rows.push({
    label: hasVariants ? 'Base' : null,
    dine: edit.basePrice ?? item.basePrice,
    dineOrig: item.basePrice,
    dineField: 'basePrice',
    take: edit.takeawayPrice ?? item.takeawayPrice ?? item.basePrice,
    takeOrig: item.takeawayPrice ?? item.basePrice,
    takeField: 'takeawayPrice',
    del: edit.deliveryPrice ?? item.deliveryPrice ?? item.basePrice,
    delOrig: item.deliveryPrice ?? item.basePrice,
    delField: 'deliveryPrice',
    variantId: undefined,
  });

  // Variant rows
  if (hasVariants) {
    for (const v of item.variants) {
      const ev = editVariants.find(e => e.id === v.id) || {};
      const origPrice = resolveVariantPrice(v);
      rows.push({
        label: v.variantName || v.name || v.type || 'Variant',
        dine: ev.variantPrice ?? origPrice,
        dineOrig: origPrice,
        dineField: 'variantPrice',
        take: ev.takeawayPrice ?? v.takeawayPrice ?? origPrice,
        takeOrig: v.takeawayPrice ?? origPrice,
        takeField: 'takeawayPrice',
        del: ev.deliveryPrice ?? v.deliveryPrice ?? origPrice,
        delOrig: v.deliveryPrice ?? origPrice,
        delField: 'deliveryPrice',
        variantId: v.id,
      });
    }
  }

  return (
    <>
      {rows.map((row, idx) => {
        const isBase = idx === 0;
        const isLastVariant = idx === rows.length - 1;

        return (
          <tr
            key={row.variantId ?? `base-${item.id}`}
            className={`
              ${isLastVariant ? 'border-b border-slate-200' : ''}
              ${!isBase ? 'bg-slate-50/60' : ''}
              ${itemEdit && isBase ? 'bg-teal-50/30' : ''}
              hover:bg-slate-50 transition-colors
            `}
          >
            {/* Item name cell — only on first row, spans all sub-rows */}
            {isBase ? (
              <td className="py-1.5 px-4 align-top" rowSpan={rows.length}>
                <div className="min-w-0">
                  <p className="text-[13px] font-medium text-slate-800 truncate leading-tight">{item.name}</p>
                  <div className="flex items-center flex-wrap gap-1 mt-0.5">
                    <span className="text-[10px] px-1.5 py-px rounded bg-slate-100 text-slate-500 font-medium">
                      {item.categoryName}
                    </span>
                    {hasVariants && item.variants.map(v => (
                      <span key={v.id} className="text-[10px] px-1.5 py-px rounded bg-teal-50 text-teal-600 font-medium">
                        {v.variantName || v.name || v.type}
                      </span>
                    ))}
                  </div>
                </div>
              </td>
            ) : null}

            {/* Label cell — always rendered for column alignment */}
            <td className="py-1 pr-2 text-right">
              {row.label && (
                <span className={`text-[10px] font-medium ${isBase ? 'text-slate-500' : 'text-slate-400'}`}>
                  {row.label}
                </span>
              )}
            </td>

            {/* Price cells */}
            <td className="py-1 px-1">
              <PriceCell value={row.dine} original={row.dineOrig} itemId={item.id} field={row.dineField} variantId={row.variantId} onCommit={onCommit} />
            </td>
            <td className="py-1 px-1">
              <PriceCell value={row.take} original={row.takeOrig} itemId={item.id} field={row.takeField} variantId={row.variantId} onCommit={onCommit} />
            </td>
            <td className="py-1 px-1">
              <PriceCell value={row.del} original={row.delOrig} itemId={item.id} field={row.delField} variantId={row.variantId} onCommit={onCommit} />
            </td>
          </tr>
        );
      })}
    </>
  );
});

// ── Main Component ──

export default function BulkPriceEditor({ categories, onClose, onSaveSuccess }) {
  const [items, setItems] = useState([]);
  const [editedPrices, setEditedPrices] = useState({});
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [adjustMode, setAdjustMode] = useState('increase');
  const [adjustPercent, setAdjustPercent] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, type: '', onConfirm: null });

  // ── Change detection ──
  const changedCount = useMemo(() => {
    let count = 0;
    for (const item of items) {
      const edit = editedPrices[item.id];
      if (!edit) continue;
      if (priceChanged(edit.basePrice, item.basePrice)) count++;
      if (priceChanged(edit.takeawayPrice, item.takeawayPrice ?? item.basePrice)) count++;
      if (priceChanged(edit.deliveryPrice, item.deliveryPrice ?? item.basePrice)) count++;
      if (edit.variants?.length && item.variants?.length) {
        for (const ev of edit.variants) {
          const orig = item.variants.find(v => v.id === ev.id);
          if (!orig) continue;
          const origPrice = resolveVariantPrice(orig);
          if (priceChanged(ev.variantPrice, origPrice)) count++;
          if (priceChanged(ev.takeawayPrice, orig.takeawayPrice ?? origPrice)) count++;
          if (priceChanged(ev.deliveryPrice, orig.deliveryPrice ?? origPrice)) count++;
        }
      }
    }
    return count;
  }, [items, editedPrices]);

  const closeConfirmDialog = useCallback(() => {
    setConfirmDialog({ isOpen: false, type: '', onConfirm: null });
  }, []);

  // ── Close with unsaved-changes guard ──
  const safeClose = useCallback(() => {
    if (changedCount > 0) {
      setConfirmDialog({
        isOpen: true,
        type: 'discard',
        onConfirm: () => { closeConfirmDialog(); onClose(); },
      });
      return;
    }
    onClose();
  }, [changedCount, onClose, closeConfirmDialog]);

  // ── Escape key (blocked while saving) ──
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape' && !saving) safeClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [safeClose, saving]);

  // ── Fetch all items once on mount ──
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    itemService.getAll()
      .then(data => {
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load menu items');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // ── Client-side filter by category + search (no API calls, edits preserved) ──
  const filteredItems = useMemo(() => {
    let result = items;
    if (categoryFilter && categoryFilter !== 'all') {
      result = result.filter(item => String(item.categoryId) === String(categoryFilter));
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(item =>
        item.name?.toLowerCase().includes(q) ||
        item.categoryName?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [items, categoryFilter, searchQuery]);

  // ── Stable handlers ──
  const handlePriceCommit = useCallback((itemId, field, value, variantId) => {
    setEditedPrices(prev => {
      const prevItem = prev[itemId] || {};
      if (!variantId) {
        return { ...prev, [itemId]: { ...prevItem, [field]: value } };
      }
      const variants = [...(prevItem.variants || [])];
      const idx = variants.findIndex(v => v.id === variantId);
      if (idx !== -1) {
        variants[idx] = { ...variants[idx], [field]: value };
      } else {
        variants.push({ id: variantId, [field]: value });
      }
      return { ...prev, [itemId]: { ...prevItem, variants } };
    });
  }, []);

  // ── Bulk % adjustment ──
  const handleApplyPercent = useCallback(() => {
    const pVal = parseFloat(adjustPercent);
    if (!pVal || pVal <= 0) return;

    if (adjustMode === 'decrease' && pVal >= 100) {
      toast.error('Decrease percentage must be less than 100%');
      return;
    }

    const factor = adjustMode === 'increase' ? (1 + pVal / 100) : (1 - pVal / 100);
    const isFiltered = searchQuery.trim() || categoryFilter !== 'all';

    setEditedPrices(prev => {
      const updated = { ...prev };
      for (const item of filteredItems) {
        const base = parseFloat(item.basePrice) || 0;
        const take = parseFloat(item.takeawayPrice ?? base);
        const del = parseFloat(item.deliveryPrice ?? base);

        updated[item.id] = {
          ...updated[item.id],
          basePrice: roundPrice(base * factor),
          takeawayPrice: roundPrice(take * factor),
          deliveryPrice: roundPrice(del * factor),
        };

        if (item.variants?.length) {
          const prevVariants = updated[item.id]?.variants || [];
          updated[item.id].variants = item.variants.map(v => {
            const vPrice = parseFloat(resolveVariantPrice(v)) || 0;
            const vTake = parseFloat(v.takeawayPrice ?? vPrice);
            const vDel = parseFloat(v.deliveryPrice ?? vPrice);
            const existing = prevVariants.find(pv => pv.id === v.id) || {};
            return {
              ...existing,
              id: v.id,
              variantPrice: roundPrice(vPrice * factor),
              takeawayPrice: roundPrice(vTake * factor),
              deliveryPrice: roundPrice(vDel * factor),
            };
          });
        }
      }
      return updated;
    });

    const sign = adjustMode === 'increase' ? '+' : '-';
    const scope = isFiltered ? `${filteredItems.length} filtered items` : `all ${filteredItems.length} items`;
    toast.success(`${sign}${pVal}% applied to ${scope}`);
  }, [adjustPercent, adjustMode, filteredItems, searchQuery, categoryFilter]);

  const handleReset = useCallback(() => {
    if (changedCount > 0) {
      setConfirmDialog({
        isOpen: true,
        type: 'reset',
        onConfirm: () => {
          closeConfirmDialog();
          setEditedPrices({});
          setAdjustPercent('');
        },
      });
      return;
    }
    setEditedPrices({});
    setAdjustPercent('');
  }, [changedCount, closeConfirmDialog]);

  // ── Save ──
  const handleSave = useCallback(async () => {
    if (changedCount === 0) return;
    setSaving(true);

    const payload = {
      items: items
        .filter(item => editedPrices[item.id])
        .map(item => {
          const edit = editedPrices[item.id];
          const changedVariants = (item.variants || [])
            .map(v => {
              const ev = (edit.variants || []).find(e => e.id === v.id);
              if (!ev) return null;
              const origPrice = resolveVariantPrice(v);
              const hasChange =
                priceChanged(ev.variantPrice, origPrice) ||
                priceChanged(ev.takeawayPrice, v.takeawayPrice ?? origPrice) ||
                priceChanged(ev.deliveryPrice, v.deliveryPrice ?? origPrice);
              if (!hasChange) return null;
              return {
                variantId: v.id,
                variantPrice: ev.variantPrice !== undefined ? Number(ev.variantPrice) : Number(origPrice),
                takeawayPrice: ev.takeawayPrice !== undefined ? Number(ev.takeawayPrice) : Number(v.takeawayPrice ?? origPrice),
                deliveryPrice: ev.deliveryPrice !== undefined ? Number(ev.deliveryPrice) : Number(v.deliveryPrice ?? origPrice),
              };
            })
            .filter(Boolean);

          return {
            menuItemId: item.id,
            basePrice: edit.basePrice !== undefined ? Number(edit.basePrice) : Number(item.basePrice),
            takeawayPrice: edit.takeawayPrice !== undefined ? Number(edit.takeawayPrice) : Number(item.takeawayPrice ?? item.basePrice),
            deliveryPrice: edit.deliveryPrice !== undefined ? Number(edit.deliveryPrice) : Number(item.deliveryPrice ?? item.basePrice),
            variants: changedVariants,
          };
        }),
    };

    try {
      const data = await itemService.bulkUpdatePrices(payload);
      if (data.success) {
        toast.success(data.message || 'Prices updated successfully');
        if (onSaveSuccess) onSaveSuccess();
        onClose();
      } else {
        toast.error(data.message || 'Failed to update prices');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [changedCount, items, editedPrices, onSaveSuccess, onClose]);

  // ── Render ──
  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={safeClose}>
      <div className="bg-white rounded-2xl w-full max-w-[1200px] max-h-[90vh] flex flex-col shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center">
              <Tags size={18} className="text-teal-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-800">Bulk Price Editor</h2>
              <p className="text-xs text-slate-400 mt-0.5">{items.length} items loaded</p>
            </div>
          </div>
          <button onClick={safeClose} className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors" aria-label="Close">
            <X size={16} className="text-slate-500" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-3 border-b border-slate-100 flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search items..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-200 w-48"
            />
          </div>

          <div className="relative">
            <Filter size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e.target.value)}
              className="pl-7 pr-8 py-1.5 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-teal-400 cursor-pointer appearance-none"
            >
              <option value="all">All categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Clear filters */}
          {(categoryFilter !== 'all' || searchQuery.trim()) && (
            <button
              type="button"
              onClick={() => { setCategoryFilter('all'); setSearchQuery(''); }}
              className="text-[11px] text-teal-600 hover:text-teal-700 font-medium hover:underline"
            >
              Clear filters
            </button>
          )}

          <div className="w-px h-6 bg-slate-200 mx-1" />

          {/* Increase / Decrease toggle */}
          <div className="flex items-center gap-1.5 bg-slate-50 rounded-lg border border-slate-200 p-0.5">
            <button
              type="button"
              onClick={() => setAdjustMode('increase')}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                adjustMode === 'increase' ? 'bg-emerald-500 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <TrendingUp size={12} />
              Increase
            </button>
            <button
              type="button"
              onClick={() => setAdjustMode('decrease')}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                adjustMode === 'decrease' ? 'bg-rose-500 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <TrendingDown size={12} />
              Decrease
            </button>
          </div>

          <div className="flex items-center gap-1.5">
            <input
              type="number" min="0" max="100" placeholder="0"
              value={adjustPercent}
              onChange={e => setAdjustPercent(e.target.value)}
              className="w-14 px-2 py-1.5 text-sm text-center border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-teal-400 font-semibold"
            />
            <span className="text-xs text-slate-500 font-medium">%</span>
            <button
              type="button"
              onClick={handleApplyPercent}
              disabled={!adjustPercent || Number(adjustPercent) <= 0}
              className="px-3 py-1.5 text-xs font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Apply
            </button>
          </div>

          <div className="ml-auto flex items-center gap-2">
            {changedCount > 0 && (
              <span className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-200 rounded-lg text-xs font-medium text-amber-700">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                {changedCount} change{changedCount !== 1 ? 's' : ''}
              </span>
            )}
            <button
              type="button"
              onClick={handleReset}
              disabled={changedCount === 0}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <RotateCcw size={12} />
              Reset
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left py-2.5 px-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-[32%]">Item</th>
                <th className="text-right py-2.5 pr-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-[8%]"></th>
                <th className="text-center py-2.5 px-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Dine-in (₹)</th>
                <th className="text-center py-2.5 px-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Takeaway (₹)</th>
                <th className="text-center py-2.5 px-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Delivery (₹)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="py-16">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm text-slate-400">Loading items...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-16">
                    <div className="flex flex-col items-center gap-2">
                      <Search size={24} className="text-slate-300" />
                      <span className="text-sm text-slate-400">
                        {searchQuery.trim()
                          ? `No items matching "${searchQuery}"`
                          : 'No items in this category'}
                      </span>
                      {searchQuery.trim() && (
                        <button
                          type="button"
                          onClick={() => setSearchQuery('')}
                          className="text-xs text-teal-600 hover:underline"
                        >
                          Clear search
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    itemEdit={editedPrices[item.id]}
                    onCommit={handlePriceCommit}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-100 flex items-center justify-between bg-white">
          <span className="text-xs text-slate-400">
            {filteredItems.length} item{filteredItems.length !== 1 ? 's' : ''} shown
          </span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={safeClose} className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || changedCount === 0}
              className="flex items-center gap-1.5 px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Save size={14} />
              {saving ? 'Saving...' : `Save${changedCount > 0 ? ` (${changedCount})` : ''}`}
            </button>
          </div>
        </div>
      </div>

    </div>

    {/* Render outside the overlay to prevent click bubbling */}
    <ConfirmDialog
      isOpen={confirmDialog.isOpen}
      onClose={closeConfirmDialog}
      onConfirm={confirmDialog.onConfirm}
      title={confirmDialog.type === 'discard' ? 'Discard Changes?' : 'Reset All Changes?'}
      message={confirmDialog.type === 'discard'
        ? `You have ${changedCount} unsaved price change${changedCount !== 1 ? 's' : ''}. These will be lost if you close now.`
        : 'This will undo all pending price changes and restore original values.'}
      confirmText={confirmDialog.type === 'discard' ? 'Discard' : 'Reset'}
      type="warning"
    />
    </>
  );
}
